/**
 * Orchestrator API Service
 * Handles all API calls for the Orchestrator Dashboard (Phase 4)
 */

import { api } from '../utils/api';

// Dashboard states for the pipeline funnel
export const DASHBOARD_STATES = [
  'INTAKE',
  'GATHERING',
  'READY_FOR_ANALYSIS',
  'ANALYZING',
  'ISSUES_PENDING',
  'READY_FOR_CALCULATION',
  'COMPLETE'
] as const;

export type DashboardState = typeof DASHBOARD_STATES[number];

export interface PipelineState {
  state: DashboardState;
  count: number;
  stuck_count: number;
}

export interface PipelineStats {
  states: PipelineState[];
  total_cases: number;
  total_stuck: number;
}

export interface CaseBlocker {
  type: 'action' | 'blocker' | 'waiting' | 'complete' | 'unknown';
  message: string;
  action: string | null;
}

export interface CaseItem {
  id: string;
  name: string;
  state: DashboardState;
  internal_state: string;
  days_in_state: number;
  is_stuck: boolean;
  blocker: CaseBlocker;
  open_issues: number;
  updated_at: string | null;
  created_at: string | null;
}

export interface CasesResponse {
  cases: CaseItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface DispatchItem {
  id: string;
  report_id: string;
  action: string;
  status: string;
  priority: string;
  trigger: string;
  affected_agents: string[];
  created_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  error_message: string | null;
}

export interface DispatchQueueResponse {
  items: DispatchItem[];
  summary: {
    pending_count: number;
    approved_count: number;
    dispatched_count: number;
    completed_today: number;
    failed_today: number;
  };
  total_returned: number;
}

export interface OrchestratorConfig {
  mode: 'MANUAL' | 'SEMI_AUTO' | 'FULL_AUTO';
  auto_analyze_on_upload: boolean;
  auto_create_issues: boolean;
  notify_on_state_change: boolean;
}

class OrchestratorApi {
  async getPipelineStats(): Promise<PipelineStats> {
    return api.get('/api/v1/orchestrator/pipeline');
  }

  async getCases(params?: {
    state?: DashboardState;
    include_complete?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CasesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.state) searchParams.append('state', params.state);
    if (params?.include_complete) searchParams.append('include_complete', 'true');
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const url = `/api/v1/orchestrator/cases${queryString ? `?${queryString}` : ''}`;

    return api.get(url);
  }

  async getDispatchQueue(params?: {
    report_id?: string;
    status?: string;
    include_completed?: boolean;
    limit?: number;
  }): Promise<DispatchQueueResponse> {
    const searchParams = new URLSearchParams();

    if (params?.report_id) searchParams.append('report_id', params.report_id);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.include_completed) searchParams.append('include_completed', 'true');
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const url = `/api/v1/orchestrator/dispatch-queue${queryString ? `?${queryString}` : ''}`;

    return api.get(url);
  }

  async approveDispatch(dispatchId: string): Promise<{ success: boolean; status: string; celery_task_id?: string }> {
    return api.post(`/api/v1/orchestrator/dispatch-queue/${dispatchId}/approve`);
  }

  async cancelDispatch(dispatchId: string): Promise<{ success: boolean; status: string }> {
    return api.post(`/api/v1/orchestrator/dispatch-queue/${dispatchId}/cancel`);
  }

  async createDispatch(reportId: string, action: string, priority: string = 'MEDIUM'): Promise<{ success: boolean; dispatch_id: string; status: string }> {
    return api.post(`/api/v1/orchestrator/cases/${reportId}/dispatch?action=${action}&priority=${priority}`);
  }

  async getConfig(): Promise<OrchestratorConfig> {
    return api.get('/api/v1/orchestrator/config');
  }

  async updateConfig(config: Partial<OrchestratorConfig>): Promise<{ success: boolean; config: OrchestratorConfig }> {
    return api.put('/api/v1/orchestrator/config', config);
  }

  async healthCheck(): Promise<{ status: string }> {
    return api.get('/api/health', { skipAuth: true });
  }
}

export const orchestratorApi = new OrchestratorApi();
export default orchestratorApi;
